import { Card, Hero, Section } from '../components/ui';

const privacyToc = [
  { id: 'introduction', number: '1', title: 'Introduction' },
  { id: 'information-we-collect', number: '2', title: 'Information We Collect' },
  { id: 'how-we-use-your-information', number: '3', title: 'How We Use Your Information' },
  { id: 'data-security', number: '4', title: 'Data Security' },
  { id: 'data-retention', number: '5', title: 'Data Retention' },
  { id: 'data-sharing', number: '6', title: 'Data Sharing and Disclosure' },
  { id: 'international-transfers', number: '7', title: 'International Data Transfers' },
  { id: 'your-rights', number: '8', title: 'Your Rights' },
  { id: 'cookies', number: '9', title: 'Cookies and Tracking' },
  { id: 'changes', number: '10', title: 'Changes to This Privacy Policy' },
  { id: 'contact', number: '11', title: 'Contact Us' }
];

export const Privacy = () => {
  return (
    <>
      <Hero title="Privacy Policy" description="Last updated: January 2025" size="sm" />

      <Section>
        <div className="legal-layout">
          <aside className="legal-sidebar">
            <Card elevation="lg" className="legal-sidebar__card">
              <h3 className="legal-sidebar__title">At a glance</h3>
              <div className="legal-sidebar__meta">
                <span>Last updated: January 2025</span>
                <span>Applies to all textadapt services</span>
              </div>
            </Card>
            <Card elevation="lg" className="legal-sidebar__card">
              <h3 className="legal-sidebar__title">Quick navigation</h3>
              <ul className="legal-sidebar__links">
                {privacyToc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>
                      <span>{item.number}</span>
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
            <Card elevation="lg" className="legal-sidebar__card">
              <h3 className="legal-sidebar__title">Need support?</h3>
              <div className="legal-sidebar__meta">
                <span>Email privacy questions to privacy@textadapt.com</span>
                <span>Contact our DPO at dpo@textadapt.com</span>
              </div>
            </Card>
          </aside>
          <article className="legal-content">
            <h2 id="introduction">1. Introduction</h2>
            <p>
              textadapt ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how
              we collect, use, disclose, and safeguard your information when you use our document processing platform
              and services.
            </p>

            <h2 id="information-we-collect">2. Information We Collect</h2>
            <h3 id="information-you-provide">2.1 Information You Provide</h3>
            <ul>
              <li>Account information (name, email, company details)</li>
              <li>Payment and billing information</li>
              <li>Documents uploaded for processing</li>
              <li>Master data (vendor catalogs, item databases)</li>
              <li>User-generated content (comments, approvals, annotations)</li>
            </ul>

            <h3 id="information-we-collect-automatically">2.2 Automatically Collected Information</h3>
            <ul>
              <li>Usage data and analytics (page views, feature usage, session duration)</li>
              <li>Device information (browser type, operating system, IP address)</li>
              <li>Performance metrics (processing times, error rates)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 id="how-we-use-your-information">3. How We Use Your Information</h2>
            <ul>
              <li>Provide and maintain our document processing services</li>
              <li>Process payments and manage subscriptions</li>
              <li>Improve our machine learning models and extraction accuracy</li>
              <li>Send service notifications, updates, and security alerts</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Detect and prevent fraud, abuse, and security incidents</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>

            <h2 id="data-security">4. Data Security</h2>
            <p>We implement industry-standard security measures to protect your data:</p>
            <ul>
              <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
              <li>Multi-tenant data isolation with organization-level boundaries</li>
              <li>Role-based access controls (RBAC) and audit logging</li>
              <li>Regular security audits and penetration testing</li>
              <li>SOC 2 Type II certified infrastructure</li>
            </ul>

            <h2 id="data-retention">5. Data Retention</h2>
            <p>
              We retain your data as long as your account is active or as needed to provide services. Processed documents
              are retained according to your organization's configured retention policy (default: 7 years for financial
              records). You may request deletion of your data at any time, subject to legal retention requirements.
            </p>

            <h2 id="data-sharing">6. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share data with:</p>
            <ul>
              <li>
                <strong>Service Providers:</strong> Cloud infrastructure (Cloudflare), payment processors (Stripe), analytics
                tools
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, subpoena, or to protect our rights
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets
              </li>
            </ul>

            <h2 id="international-transfers">7. International Data Transfers</h2>
            <p>
              Your data may be processed in the United States and other jurisdictions where our service providers operate.
              We use Standard Contractual Clauses (SCCs) and other legal mechanisms to ensure adequate protection for
              international transfers.
            </p>

            <h2 id="your-rights">8. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul>
              <li>Access, correct, or delete your personal information</li>
              <li>Object to or restrict certain processing activities</li>
              <li>Data portability (receive your data in a structured format)</li>
              <li>Withdraw consent where processing is based on consent</li>
              <li>Lodge a complaint with a data protection authority</li>
            </ul>

            <h2 id="cookies">9. Cookies and Tracking</h2>
            <p>
              We use essential cookies for authentication and session management, as well as optional analytics cookies to
              improve our services. You can manage cookie preferences through your browser settings.
            </p>

            <h2 id="changes">10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes via email or
              through the platform. Continued use after changes constitutes acceptance of the updated policy.
            </p>

            <h2 id="contact">11. Contact Us</h2>
            <p>For questions about this Privacy Policy or to exercise your data rights, contact us at:</p>
            <ul className="legal-list-plain">
              <li>
                <strong>Email:</strong> <a href="mailto:privacy@textadapt.com">privacy@textadapt.com</a>
              </li>
              <li>
                <strong>Address:</strong> textadapt Inc., 123 Enterprise Ave, San Francisco, CA 94105
              </li>
              <li>
                <strong>Data Protection Officer:</strong> <a href="mailto:dpo@textadapt.com">dpo@textadapt.com</a>
              </li>
            </ul>
          </article>
        </div>
      </Section>
    </>
  );
};
